import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, Mail, Loader2, Users, Save as SaveIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ManualRecipientsModal — the THIRD outstanding-email dispatch pipeline
 * (Feb 2026). Runs alongside (never replaces) category-subscribers and
 * salesman-recipients.
 *
 * Each entry is an arbitrary email list + a filter selection:
 *   • Divisions (empty = All divisions)
 *   • Salesmen  (empty = All salesmen)
 * On every fresh Excel upload, each recipient gets an email with the
 * rows matching BOTH selections. "All / All" gets the full report.
 */
const emptyDraft = () => ({ id: null, label: '', emails: '', categories: [], salesmen: [] });

// ---------------------------------------------------------------------------
// Sub-components hoisted to MODULE SCOPE (Feb 2026 bug fix).
//
// Previously `FilterPill` and `RecipientEditor` were defined INSIDE
// `ManualRecipientsModal`. Every parent state change (`setDrafts` on
// each keystroke) produced a NEW function identity for those inner
// components, so React saw a different component type on every
// re-render, unmounted the entire editor subtree, and remounted a
// fresh one — the <Input> lost DOM focus after the first character
// and the user could only ever type one letter at a time.
//
// Defining them at module scope keeps the component type stable
// across re-renders, so React reconciles the existing DOM (and its
// focus) instead of tearing down and rebuilding.
// ---------------------------------------------------------------------------
const toggle = (list, value) => list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const FilterPill = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-xs px-2.5 py-1 rounded-full border transition ${active ? 'bg-[#0078D4] border-[#0078D4] text-white font-semibold' : 'bg-white border-neutral-200 text-neutral-600 hover:border-[#0078D4]'}`}
    data-testid={testId}
  >
    {label}
  </button>
);

const RecipientEditor = ({ draft, onChange, testIdPrefix, knownCategories, knownSalesmen }) => (
  <>
    <div className="flex gap-2 flex-wrap items-center">
      <div className="flex-1 min-w-[160px]">
        <label className="text-[11px] uppercase tracking-wider text-neutral-500">Label (optional)</label>
        <Input
          value={draft.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="Sales manager summary"
          className="text-sm mt-0.5"
          data-testid={`${testIdPrefix}-label`}
        />
      </div>
      <div className="flex-[2] min-w-[220px]">
        <label className="text-[11px] uppercase tracking-wider text-neutral-500">Emails (comma-separated)</label>
        <Input
          value={draft.emails}
          onChange={(e) => onChange({ emails: e.target.value })}
          placeholder="one@mrpco.net, two@mrpco.net"
          className="text-sm mt-0.5"
          data-testid={`${testIdPrefix}-emails`}
        />
      </div>
    </div>
    <div>
      <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">
        Divisions {(draft.categories || []).length === 0 && <span className="ml-1 text-emerald-600 lowercase">· all divisions</span>}
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-divisions`}>
        <FilterPill
          label="All divisions"
          active={(draft.categories || []).length === 0}
          onClick={() => onChange({ categories: [] })}
          testId={`${testIdPrefix}-division-all`}
        />
        {knownCategories.map((c) => (
          <FilterPill
            key={c}
            label={c.charAt(0).toUpperCase() + c.slice(1)}
            active={(draft.categories || []).includes(c)}
            onClick={() => onChange({ categories: toggle(draft.categories || [], c) })}
            testId={`${testIdPrefix}-division-${c}`}
          />
        ))}
      </div>
    </div>
    <div>
      <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">
        Salesmen {(draft.salesmen || []).length === 0 && <span className="ml-1 text-emerald-600 lowercase">· all salesmen</span>}
      </div>
      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" data-testid={`${testIdPrefix}-salesmen`}>
        <FilterPill
          label="All salesmen"
          active={(draft.salesmen || []).length === 0}
          onClick={() => onChange({ salesmen: [] })}
          testId={`${testIdPrefix}-salesman-all`}
        />
        {knownSalesmen.map((s) => (
          <FilterPill
            key={s}
            label={s}
            active={(draft.salesmen || []).includes(s)}
            onClick={() => onChange({ salesmen: toggle(draft.salesmen || [], s) })}
            testId={`${testIdPrefix}-salesman-${s}`}
          />
        ))}
      </div>
    </div>
  </>
);

const ManualRecipientsModal = ({ onClose, authHeaders }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);   // id being saved
  const [items, setItems] = useState([]);       // server truth
  const [drafts, setDrafts] = useState({});     // id → draft copy
  const [knownCategories, setKnownCategories] = useState([]);
  const [knownSalesmen, setKnownSalesmen] = useState([]);
  const [newDraft, setNewDraft] = useState(emptyDraft());
  const [search, setSearch] = useState('');    // Feb 2026 — real-time filter by label / email
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/outstanding/manual-recipients`, { headers: authHeaders });
      setItems(r.data.recipients || []);
      setKnownCategories(r.data.known_categories || []);
      setKnownSalesmen(r.data.known_salesmen || []);
      const d = {};
      (r.data.recipients || []).forEach((it) => {
        d[it.id] = {
          id: it.id,
          label: it.label || '',
          emails: (it.emails || []).join(', '),
          categories: it.categories || [],
          salesmen: it.salesmen || [],
        };
      });
      setDrafts(d);
    } catch (e) {
      toast({ title: 'Could not load recipients', description: e.response?.data?.detail || 'Try again.' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const parseEmails = (draft) => (draft || '')
    .split(/[,;\s]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0);

  const buildBody = (d) => ({
    label: (d.label || '').trim(),
    emails: parseEmails(d.emails),
    categories: d.categories || [],
    salesmen: d.salesmen || [],
  });

  const save = async (id) => {
    const d = drafts[id];
    const body = buildBody(d);
    if (body.emails.length === 0) {
      toast({ title: 'Add at least one email' });
      return;
    }
    setSaving(id);
    try {
      await axios.put(`${API}/admin/outstanding/manual-recipients/${id}`, body, { headers: authHeaders });
      toast({ title: 'Saved', description: `${body.emails.length} email(s)` });
      await load();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setSaving(null); }
  };

  const create = async () => {
    const body = buildBody(newDraft);
    if (body.emails.length === 0) {
      toast({ title: 'Add at least one email' });
      return;
    }
    setSaving('__new__');
    try {
      await axios.post(`${API}/admin/outstanding/manual-recipients`, body, { headers: authHeaders });
      toast({ title: 'Recipient added', description: `${body.emails.length} email(s)` });
      setNewDraft(emptyDraft());
      await load();
    } catch (e) {
      toast({ title: 'Create failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setSaving(null); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this recipient? They will stop receiving digests immediately.')) return;
    setSaving(id);
    try {
      await axios.delete(`${API}/admin/outstanding/manual-recipients/${id}`, { headers: authHeaders });
      toast({ title: 'Removed' });
      await load();
    } catch (e) {
      toast({ title: 'Remove failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setSaving(null); }
  };

  const patchDraft = (id, patch) => setDrafts((p) => ({ ...p, [id]: { ...p[id], ...patch } }));
  const patchNewDraft = (patch) => setNewDraft((p) => ({ ...p, ...patch }));

  // Feb 2026 — real-time filter as admin types in the search bar.
  // Matches on label OR any email in the recipient's draft (which
  // mirrors server truth after every save/load), case-insensitive.
  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const d = drafts[it.id] || {};
      if ((d.label || '').toLowerCase().includes(q)) return true;
      if ((d.emails || '').toLowerCase().includes(q)) return true;
      // Fall back to server-truth values in case draft isn't seeded yet.
      if ((it.label || '').toLowerCase().includes(q)) return true;
      return (it.emails || []).some((e) => (e || '').toLowerCase().includes(q));
    });
  }, [items, drafts, search]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="outstanding-manual-recipients-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between rounded-t-3xl z-10">
          <div>
            <h3 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
              <Users className="w-5 h-5 text-[#0078D4]" /> Manual recipients
            </h3>
            <p className="text-xs text-neutral-500">
              Send a filtered outstanding digest to any address after every upload. Independent of category-subs and salesman-recipient pipelines.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full" data-testid="outstanding-manual-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4" data-testid="outstanding-manual-recipients-list">
          {/* Add new */}
          <div className="rounded-2xl border-2 border-dashed border-[#0078D4]/40 bg-[#DEECF9]/10 p-4 space-y-3">
            <div className="text-sm font-semibold text-[#0078D4] flex items-center gap-1">
              <Plus className="w-4 h-4" /> Add new recipient
            </div>
            <RecipientEditor
              draft={newDraft}
              onChange={patchNewDraft}
              testIdPrefix="outstanding-manual-new"
              knownCategories={knownCategories}
              knownSalesmen={knownSalesmen}
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={create}
                disabled={saving === '__new__'}
                className="bg-[#0078D4] hover:bg-[#005A9E] text-white rounded-full"
                data-testid="outstanding-manual-create-btn"
              >
                {saving === '__new__' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add recipient</>}
              </Button>
            </div>
          </div>

          {loading && (
            <div className="text-center py-6 text-neutral-500">
              <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="text-center py-6 text-neutral-400 text-sm">
              No manual recipients yet. Add the first one above.
            </div>
          )}

          {/* Feb 2026 — real-time search bar. Filters saved recipients
              below by label or email as the admin types. Hidden until at
              least one recipient exists to keep the "empty state" clean. */}
          {!loading && items.length > 0 && (
            <div className="relative pt-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search recipients by label or email…"
                className="text-sm pr-16"
                data-testid="outstanding-manual-search-input"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 mt-1 text-[11px] text-neutral-500 hover:text-[#0078D4] px-2 py-0.5"
                  data-testid="outstanding-manual-search-clear"
                >
                  Clear
                </button>
              )}
              <p className="text-[11px] text-neutral-500 mt-1">
                Showing {visibleItems.length} of {items.length}
              </p>
            </div>
          )}

          {!loading && items.length > 0 && visibleItems.length === 0 && (
            <div className="text-center py-6 text-neutral-400 text-sm" data-testid="outstanding-manual-search-empty">
              No recipients match &quot;{search}&quot;.
            </div>
          )}

          {!loading && visibleItems.map((it) => (
            <div
              key={it.id}
              className="rounded-2xl border border-neutral-200 bg-white p-4 space-y-3"
              data-testid={`outstanding-manual-row-${it.id}`}
            >
              <RecipientEditor
                draft={drafts[it.id] || emptyDraft()}
                onChange={(patch) => patchDraft(it.id, patch)}
                testIdPrefix={`outstanding-manual-${it.id}`}
                knownCategories={knownCategories}
                knownSalesmen={knownSalesmen}
              />
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => remove(it.id)}
                  disabled={saving === it.id}
                  className="rounded-full text-red-600 hover:bg-red-50 border-red-200"
                  data-testid={`outstanding-manual-delete-btn-${it.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  onClick={() => save(it.id)}
                  disabled={saving === it.id}
                  className="bg-[#0078D4] hover:bg-[#005A9E] text-white rounded-full"
                  data-testid={`outstanding-manual-save-btn-${it.id}`}
                >
                  {saving === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><SaveIcon className="w-4 h-4 mr-1" /> Save</>}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ManualRecipientsModal;
